// Billing and export, as the web app sees them (phase 6).
import { request, type UserRole } from './api';

export type SubscriptionStatus = 'trialing' | 'active' | 'read_only' | 'cancelled';

/**
 * A seat's role is a user's role — the price card has one line per role, and
 * the ledger counts the same three. This was its own identical union, which is
 * two lists that have to agree with each other and with the server's, and
 * nothing making them.
 */
export type SeatRole = UserRole;

export interface PriceCard {
  currency: string;
  /**
   * True since 2026-09-04: `priceCents` is the whole price with GST inside it,
   * and every `gstCents` the API reports is the GST contained in a total, not
   * an amount added on top.
   */
  pricesIncludeGst: boolean;
  gstRegistered: boolean;
  gstRate: number;
  seats: { role: SeatRole; label: string; priceCents: number }[];
}

export interface InvoiceLine {
  role: SeatRole;
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
}

/**
 * An adjustment note under Div 19 of the GST Act. It offsets an invoice and
 * never edits one, so it arrives attached to the invoice it adjusts.
 */
export interface CreditNote {
  id: string;
  number: string;
  reason: string;
  issuedAt: string;
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
  gstRegistered: boolean;
}

export interface Invoice {
  id: string;
  number: string | null;
  period: string;
  status: 'draft' | 'open' | 'paid' | 'failed' | 'void';
  issuedAt: string | null;
  paidAt: string | null;
  subtotalCents: number;
  discountCents: number;
  gstCents: number;
  totalCents: number;
  gstRegistered: boolean;
  issuerName: string | null;
  issuerAbn: string | null;
  lines: InvoiceLine[];
  creditNotes: CreditNote[];
}

export interface BillingOverview {
  status: SubscriptionStatus;
  trialEndsAt: string;
  card: { brand: string; last4: string; expMonth: number; expYear: number } | null;
  discountPercent: number;
  foundingMember: boolean;
  cancelledAt: string | null;
  liveUntil: string | null;
  dataDeletedOn: string | null;
  nextBill: {
    month: string;
    seats: { role: SeatRole; label: string; quantity: number; amountCents: number }[];
    subtotalCents: number;
    discountCents: number;
    gstCents: number;
    totalCents: number;
    estimate: boolean;
  };
  invoices: Invoice[];
}

export interface ExportRecord {
  id: string;
  status: 'pending' | 'running' | 'ready' | 'failed' | 'expired';
  requestedAt: string;
  finishedAt: string | null;
  expiresAt: string | null;
  recordCount: number | null;
  fileCount: number | null;
  sizeBytes: number | null;
}

export interface ExportOverview {
  inProgress: boolean;
  download: boolean;
  exports: ExportRecord[];
}

export const billing = {
  prices: () => request<PriceCard>('/billing/prices'),
  overview: () => request<BillingOverview>('/billing'),
  startCardSetup: () => request<{ url: string }>('/billing/card', { method: 'POST' }),
  confirmCard: (sessionId: string) =>
    request<{ status: SubscriptionStatus; billingStartsOn: string }>('/billing/card/confirm', {
      method: 'POST',
      body: { sessionId },
    }),
  removeCard: () => request<{ card: null }>('/billing/card', { method: 'DELETE' }),
  cancel: () =>
    request<{ cancelledAt: string; liveUntil: string; dataDeletedOn: string }>('/billing/cancel', {
      method: 'POST',
    }),
  reactivate: () =>
    request<{ status: SubscriptionStatus }>('/billing/reactivate', { method: 'POST' }),

  exports: () => request<ExportOverview>('/exports'),
  requestExport: () => request<{ export: ExportRecord }>('/exports', { method: 'POST' }),
};

/** '$20.00'. The server sends cents; nothing in the UI does money arithmetic. */
export function money(cents: number): string {
  const sign = cents < 0 ? '-' : '';
  const absolute = Math.abs(cents);
  return `${sign}$${Math.trunc(absolute / 100).toLocaleString('en-AU')}.${String(
    absolute % 100
  ).padStart(2, '0')}`;
}

/** 'August 2026' from a '2026-08-01' month start. */
export function monthName(month: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month.slice(0, 10)}T00:00:00Z`));
}

/** '19/08/2026' (round 45: every date reads DD/MM/YYYY). */
export function longDate(value: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date(value));
}

export function fileSize(bytes: number): string {
  const units = ['bytes', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${unit === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}
